import { ComponentPropsWithoutRef } from "react";
import { twMerge } from "tailwind-merge";
import { StatusAgendamentoEnum } from "../../../enums/statusAgendamento";
import { ClockIcon } from "../ClockIcon";
import { formatStatusLabel } from "./formatStatusLabel";

const STATUS_COLOR_CLASSES: Record<StatusAgendamentoEnum, string> = {
  [StatusAgendamentoEnum.ABERTO]: "bg-[#31cbec]/10 border-[#31cbec] text-[#31cbec]",
  [StatusAgendamentoEnum.AGUARDANDO_PAGAMENTO]:
    "bg-[#d0c808]/10 border-[#d0c808] text-[#d0c808]",
  [StatusAgendamentoEnum.AGENDADO]: "bg-[#558fff]/10 border-[#558fff] text-[#558fff]",
  [StatusAgendamentoEnum.FINALIZADO]: "bg-[#1bbb48]/10 border-[#1bbb48] text-[#1bbb48]",
  [StatusAgendamentoEnum.INICIADO]: "bg-[#0025a8]/10 border-[#0025a8] text-[#0025a8]",
  [StatusAgendamentoEnum.CANCELADO]: "bg-[#e42e30]/10 border-[#e42e30] text-[#e42e30]",
  [StatusAgendamentoEnum.PAGO]: "bg-[#1bbb48]/10 border-[#1bbb48] text-[#1bbb48]",
  [StatusAgendamentoEnum.REEMBOLSADO]: "bg-[#ee6f27]/10 border-[#ee6f27] text-[#ee6f27]",
};

// Details-page overrides (Figma "DETALHES DO AGENDAMENTO"): Agendado is green
// with a clock icon there, while the listing keeps it blue.
const DETAIL_STATUS_COLOR_CLASSES: Partial<Record<StatusAgendamentoEnum, string>> = {
  [StatusAgendamentoEnum.AGENDADO]: "bg-[#38b449]/10 border-[#38b449] text-[#0d9934]",
};

const DETAIL_STATUS_WITH_ICON = [StatusAgendamentoEnum.AGENDADO];

interface IStatusTagV2Props extends ComponentPropsWithoutRef<"span"> {
  status: string;
  /** `detail` applies the details-page palette/icon; omit for the listing look. */
  variant?: "default" | "detail";
}

/**
 * Pill for a `StatusAgendamentoEnum` value. Default variant is the listing
 * look; `variant="detail"` is the agendamento details page look (Agendado in
 * green with a clock icon) without changing the listing.
 */
export const StatusTagV2 = ({
  status,
  variant = "default",
  className,
  ...rest
}: IStatusTagV2Props) => {
  const isDetail = variant === "detail";
  const colorClasses =
    (isDetail && DETAIL_STATUS_COLOR_CLASSES[status as StatusAgendamentoEnum]) ||
    STATUS_COLOR_CLASSES[status as StatusAgendamentoEnum];
  const hasClockIcon =
    isDetail && DETAIL_STATUS_WITH_ICON.includes(status as StatusAgendamentoEnum);

  return (
    <span
      {...rest}
      className={twMerge(
        "inline-flex items-center justify-center rounded-full border border-solid px-2.5 py-1 font-['Mulish'] text-sm font-normal whitespace-nowrap",
        isDetail && "gap-1.5 text-xs",
        colorClasses,
        className,
      )}
    >
      {hasClockIcon && (
        <ClockIcon className="h-3 w-3" />
      )}
      {formatStatusLabel(status)}
    </span>
  );
};
